/**
 * Query parameter tracking utility for GWP eligibility gating.
 * Records query params seen during the user's journey in localStorage.
 * Provides helpers to check whether a given key/value pair has been visited.
 */

/** @typedef {{ [param: string]: { [value: string]: string } }} VisitedParams */

export const GWP_VISITED_PARAMS_STORAGE_KEY = 'gwp:visitedQueryParams';

/**
 * Initializes query parameter tracking by recording the current page's query params.
 * Safe to call multiple times; will merge values.
 * @param {{ storageKey?: string }=} options - Optional configuration.
 * @returns {void}
 */
export function initQueryParamTracker(options) {
  if (typeof window === 'undefined') return;
  const storageKey = options && typeof options.storageKey === 'string' && options.storageKey.length > 0 ? options.storageKey : GWP_VISITED_PARAMS_STORAGE_KEY;
  try {
    recordCurrentQueryParams({ storageKey });
  } catch (err) {
    // Swallow errors to avoid impacting the shopping journey.

    console.warn('[GWP] query tracker init failed.', err);
  }
}

/**
 * Records the current page's query parameters into localStorage.
 * Stores the ISO timestamp of the last time each key/value was seen.
 * @param {{ storageKey?: string }=} options - Optional configuration.
 * @returns {void}
 */
export function recordCurrentQueryParams(options) {
  if (typeof window === 'undefined') return;
  const storageKey = options && typeof options.storageKey === 'string' && options.storageKey.length > 0 ? options.storageKey : GWP_VISITED_PARAMS_STORAGE_KEY;

  const search = window.location && typeof window.location.search === 'string' ? window.location.search : '';
  if (search.length === 0) return;

  const params = new URLSearchParams(search);
  if (params.toString().length === 0) return;

  const store = readVisitedParams(storageKey);
  const nowIso = new Date().toISOString();

  params.forEach((value, key) => {
    const k = String(key || '').trim();
    const v = String(value || '').trim();
    if (k.length === 0 || v.length === 0) return;

    if (!store[k]) store[k] = {};
    store[k][v] = nowIso;
  });

  writeVisitedParams(storageKey, store);
}

/**
 * Returns true if the user has visited any page with the specified key/value pair.
 * @param {{ key: string, value: string, storageKey?: string }} options - Param to check and optional storage key.
 * @returns {boolean}
 */
export function hasVisitedParam(options) {
  const key = options && typeof options.key === 'string' ? options.key : '';
  const value = options && typeof options.value === 'string' ? options.value : '';
  if (key.length === 0 || value.length === 0) return false;

  const storageKey = options && typeof options.storageKey === 'string' && options.storageKey.length > 0 ? options.storageKey : GWP_VISITED_PARAMS_STORAGE_KEY;
  const store = readVisitedParams(storageKey);
  const values = store[key] || null;
  if (!values) return false;
  return typeof values[value] === 'string' && values[value].length > 0;
}

/**
 * Reads visited params map from localStorage.
 * @param {string} storageKey - Storage key.
 * @returns {VisitedParams}
 */
export function readVisitedParams(storageKey) {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (typeof raw !== 'string' || raw.length === 0) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return /** @type {VisitedParams} */ (parsed);
  } catch (err) {
    console.warn('[GWP] query tracker read failed.', err);
    return {};
  }
}

/**
 * Writes visited params map to localStorage.
 * @param {string} storageKey - Storage key.
 * @param {VisitedParams} data - Data to persist.
 * @returns {void}
 */
export function writeVisitedParams(storageKey, data) {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(data));
  } catch (err) {
    console.warn('[GWP] query tracker write failed.', err);
  }
}